import { c as _c } from "react/compiler-runtime";
import { useViewPort } from "@website/hooks/viewport";
import { MaterialIcon } from "@website/icons/material";
import { Button, ButtonColorType, ButtonSizeType, ButtonVariantType } from "@website/ui/button";
import { numberConvert } from "@website/utils/number-convert";
import { CheckboxFilter, Input, RadioFilter, RangeFilter, SwitchFilter } from "@website/widget/filter/filter-components";
import { FilterMap } from "@website/widget/filter/filter-map";
import { useCityFilterState, useFilterLayoutEffect, useFilterTracking, useFilters } from "@website/widget/filter/filter.hook";
import type { FiltersSchemaType, MapFilterProperties } from "@website/widget/filter/filter.types";
import styles from "@website/widget/filter/index.module.scss";
import { useHandleScroll } from "@website/widget/filter/scroll.hook";
import { Children, Fragment, useRef } from "react";
interface ResetButtonProperties {
  size: ButtonSizeType;
  color: ButtonColorType;
  variant: ButtonVariantType;
}
interface FilterProperties {
  schema: FiltersSchemaType;
  title: string;
  loading?: boolean;
  filtersActionTitle: string;
  resetButtonProps: ResetButtonProperties;
  className?: string;
  filtersClassName?: string;
  mapFilterProps?: MapFilterProperties;
  scroll?: boolean;
  trackingCallback?({
    removed
  }: {
    removed: object;
  }): void;
  dontSyncScroll?: boolean;
  isNewPlP?: boolean;
}
export const Filters = properties => {
  const $ = _c(86);
  const {
    schema,
    loading,
    title,
    filtersActionTitle,
    resetButtonProps,
    className,
    filtersClassName,
    mapFilterProps,
    scroll,
    trackingCallback,
    dontSyncScroll,
    isNewPlP
  } = properties;
  const containerReference = useRef(null);
  const {
    isMobile
  } = useViewPort();
  let t0;
  if ($[0] !== dontSyncScroll) {
    t0 = {
      dontSyncScroll
    };
    $[0] = dontSyncScroll;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    formRef
  } = useFilterLayoutEffect(t0);
  let t1;
  if ($[2] !== schema) {
    t1 = {
      schema
    };
    $[2] = schema;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    selectedFiltersCount,
    filterKeys,
    clearFilters
  } = useFilters(t1);
  const {
    onCloseFilters,
    visible
  } = useCityFilterState();
  let t2;
  if ($[4] !== filterKeys || $[5] !== scroll) {
    t2 = {
      scroll,
      containerReference,
      filterKeys
    };
    $[4] = filterKeys;
    $[5] = scroll;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useHandleScroll(t2);
  let t3;
  if ($[7] !== schema || $[8] !== trackingCallback) {
    t3 = {
      callback: trackingCallback,
      schema
    };
    $[7] = schema;
    $[8] = trackingCallback;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  useFilterTracking(t3);
  let t4;
  if ($[10] !== schema) {
    t4 = Object.entries(schema);
    $[10] = schema;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const filtersSchemaEntries = t4;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[12] !== className || $[13] !== clearFilters || $[14] !== filtersClassName || $[15] !== filtersSchemaEntries || $[16] !== formRef || $[17] !== isMobile || $[18] !== isNewPlP || $[19] !== mapFilterProps || $[20] !== onCloseFilters || $[21] !== resetButtonProps || $[22] !== selectedFiltersCount || $[23] !== title || $[24] !== visible) {
    const children = filtersSchemaEntries.map(_temp).filter(Boolean);
    const isDialog = visible && (isMobile || isNewPlP);
    if ($[35] !== isDialog || $[36] !== onCloseFilters) {
      t14 = isDialog && <button aria-label={"\u0628\u0633\u062A\u0646 \u0641\u06CC\u0644\u062A\u0631\u0647\u0627"} className={styles.backdrop} onClick={onCloseFilters} type="button" />;
      $[35] = isDialog;
      $[36] = onCloseFilters;
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    t10 = `${styles["panel-container"]} ${visible ? styles.visible : ""} ${isNewPlP ? styles["mobile-view"] : ""} ${className ?? ""}`;
    t11 = isDialog ? "dialog" : undefined;
    t12 = containerReference;
    if ($[38] !== isNewPlP || $[39] !== mapFilterProps) {
      t13 = mapFilterProps && !isNewPlP && <FilterMap label={mapFilterProps.label} onClick={mapFilterProps.onClick} />;
      $[38] = isNewPlP;
      $[39] = mapFilterProps;
      $[40] = t13;
    } else {
      t13 = $[40];
    }
    t8 = `${styles.filters} ${filtersClassName ?? ""}`;
    const t15 = isMobile ? "arrow_forward" : "close";
    let t16;
    if ($[41] !== t15) {
      t16 = <MaterialIcon className="text-on-surface-medium-emphasis text-2xl" name={t15} />;
      $[41] = t15;
      $[42] = t16;
    } else {
      t16 = $[42];
    }
    let t17;
    if ($[43] !== onCloseFilters || $[44] !== t16) {
      t17 = <Button className={styles["close-button"]} color="neutral" onClick={onCloseFilters} variant="text">{t16}</Button>;
      $[43] = onCloseFilters;
      $[44] = t16;
      $[45] = t17;
    } else {
      t17 = $[45];
    }
    let t18;
    if ($[46] !== title) {
      t18 = <span className={styles["filters-header-title"]}>{title}</span>;
      $[46] = title;
      $[47] = t18;
    } else {
      t18 = $[47];
    }
    let t19;
    if ($[48] !== t17 || $[49] !== t18) {
      t19 = <div className={styles["filters-title-container"]}>{t17}{t18}</div>;
      $[48] = t17;
      $[49] = t18;
      $[50] = t19;
    } else {
      t19 = $[50];
    }
    let t20;
    if ($[51] !== selectedFiltersCount) {
      t20 = Number(selectedFiltersCount) ? <MaterialIcon name="delete_outline" /> : null;
      $[51] = selectedFiltersCount;
      $[52] = t20;
    } else {
      t20 = $[52];
    }
    const t21 = !selectedFiltersCount;
    let t22;
    if ($[53] !== selectedFiltersCount) {
      t22 = selectedFiltersCount ? `حذف فیلترها (${numberConvert(selectedFiltersCount, {
        locale: "fa"
      })})` : "\u062D\u0630\u0641 \u0641\u06CC\u0644\u062A\u0631\u0647\u0627";
      $[53] = selectedFiltersCount;
      $[54] = t22;
    } else {
      t22 = $[54];
    }
    let t23;
    if ($[55] !== clearFilters || $[56] !== resetButtonProps || $[57] !== t20 || $[58] !== t21 || $[59] !== t22) {
      t23 = <Button {...resetButtonProps} StartIcon={t20} className="h-16" hidden={t21} onClick={clearFilters}>{t22}</Button>;
      $[55] = clearFilters;
      $[56] = resetButtonProps;
      $[57] = t20;
      $[58] = t21;
      $[59] = t22;
      $[60] = t23;
    } else {
      t23 = $[60];
    }
    if ($[61] !== t19 || $[62] !== t23) {
      t9 = <div className={styles["filters-header-box"]}>{t19}{t23}</div>;
      $[61] = t19;
      $[62] = t23;
      $[63] = t9;
    } else {
      t9 = $[63];
    }
    t5 = styles["filters-form-container"];
    t6 = formRef;
    t7 = Children.map(children, (child, index) => <Fragment key={child?.key ?? index}>{child}{Children.count(children) - 1 !== index && <div className={styles.divider} />}</Fragment>);
    $[12] = className;
    $[13] = clearFilters;
    $[14] = filtersClassName;
    $[15] = filtersSchemaEntries;
    $[16] = formRef;
    $[17] = isMobile;
    $[18] = isNewPlP;
    $[19] = mapFilterProps;
    $[20] = onCloseFilters;
    $[21] = resetButtonProps;
    $[22] = selectedFiltersCount;
    $[23] = title;
    $[24] = visible;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    t10 = $[25];
    t11 = $[26];
    t12 = $[27];
    t13 = $[28];
    t14 = $[29];
    t5 = $[30];
    t6 = $[31];
    t7 = $[32];
    t8 = $[33];
    t9 = $[34];
  }
  let t15;
  if ($[64] !== t5 || $[65] !== t6 || $[66] !== t7) {
    t15 = <div className={t5} ref={t6}>{t7}</div>;
    $[64] = t5;
    $[65] = t6;
    $[66] = t7;
    $[67] = t15;
  } else {
    t15 = $[67];
  }
  let t16;
  if ($[68] !== filtersActionTitle || $[69] !== loading || $[70] !== onCloseFilters) {
    t16 = <div className={styles["filters-footer-container"]}><Button color="secondary" fullWidth={true} loading={loading} onClick={onCloseFilters} size="large">{filtersActionTitle}</Button></div>;
    $[68] = filtersActionTitle;
    $[69] = loading;
    $[70] = onCloseFilters;
    $[71] = t16;
  } else {
    t16 = $[71];
  }
  let t17;
  if ($[72] !== t15 || $[73] !== t16 || $[74] !== t8 || $[75] !== t9) {
    t17 = <div className={t8}>{t9}{t15}{t16}</div>;
    $[72] = t15;
    $[73] = t16;
    $[74] = t8;
    $[75] = t9;
    $[76] = t17;
  } else {
    t17 = $[76];
  }
  let t18;
  if ($[77] !== t10 || $[78] !== t11 || $[79] !== t12 || $[80] !== t13 || $[81] !== t17) {
    t18 = <aside className={t10} role={t11} ref={t12}>{t13}{t17}</aside>;
    $[77] = t10;
    $[78] = t11;
    $[79] = t12;
    $[80] = t13;
    $[81] = t17;
    $[82] = t18;
  } else {
    t18 = $[82];
  }
  let t19;
  if ($[83] !== t14 || $[84] !== t18) {
    t19 = <>{t14}{t18}</>;
    $[83] = t14;
    $[84] = t18;
    $[85] = t19;
  } else {
    t19 = $[85];
  }
  return t19;
};
function _temp(t0) {
  const [name, schema_0] = t0;
  switch (schema_0.type) {
    case "range":
      {
        return <RangeFilter key={name} {...schema_0} />;
      }
    case "input":
      {
        return <Input key={name} name={name} {...schema_0} />;
      }
    case "switch":
      {
        return <SwitchFilter key={name} name={name} {...schema_0} />;
      }
    case "text":
      {
        return null;
      }
    case "radio":
      {
        return <RadioFilter key={name} name={name} {...schema_0} />;
      }
    case "checkbox":
      {
        return <CheckboxFilter key={name} name={name} {...schema_0} />;
      }
    case "custom":
      {
        return <Fragment key={name}>{schema_0.component}</Fragment>;
      }
    default:
      {
        return null;
      }
  }
}